/** Reseller CDRs page with margin breakdown and export. */
import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
} from "@mui/material";
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  DateRange as DateIcon,
  PhoneDisabled as PhoneDisabledIcon,
} from "@mui/icons-material";
import { format, subDays } from "date-fns";
import { API_BASE_URL, resellerFetch } from "../../api/client";

/** CDR interface. */
interface CDR {
  id: string;
  call_id: string;
  caller: string;
  callee: string;
  answer_time: string;
  duration: number;
  billsec: number;
  hangup_cause: string;
  account: string;
  cost: number;
  margin_breakdown?: {
    cost: number;
    margin_amount: number;
    total_cost: number;
    margin_percent: number;
  };
}

/** Summary statistics. */
interface SummaryStats {
  total_calls: number;
  total_revenue: number;
  total_margin: number;
}

/** Reseller CDRs page component. */
export default function ResellerCDRsPage() {
  const [cdrs, setCDRs] = useState<CDR[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCDRs, setTotalCDRs] = useState(0);
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 7), "yyyy-MM-dd"));
  const [endDate, setEndDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [accountFilter, setAccountFilter] = useState("");
  const [exporting, setExporting] = useState(false);
  const [summary, setSummary] = useState<SummaryStats>({
    total_calls: 0,
    total_revenue: 0,
    total_margin: 0,
  });

  /** Fetch CDRs from API. */
  const fetchCDRs = async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
        page: (page + 1).toString(),
        page_size: rowsPerPage.toString(),
      });

      if (accountFilter) {
        params.append("account_filter", accountFilter);
      }

      const response = await resellerFetch(
        `${API_BASE_URL}/api/v1/resellers/me/cdrs?${params}`
      );

      if (!response.ok) {
        throw new Error("Failed to load CDRs");
      }

      const data = await response.json();
      setCDRs(data.cdrs || []);
      setTotalCDRs(data.total || 0);

      // Phase 7.4: prefer the server-computed summary (aggregate over the
      // FULL filtered set). The old page-level reduce undercounted revenue
      // whenever total > page_size — keep it only as a legacy fallback.
      if (data.summary) {
        setSummary({
          total_calls: data.summary.total_calls ?? data.total ?? 0,
          total_revenue: data.summary.total_revenue ?? 0,
          total_margin: data.summary.total_margin ?? 0,
        });
      } else {
        const totalRevenue = (data.cdrs || []).reduce(
          (sum: number, cdr: CDR) =>
            sum + (cdr.margin_breakdown?.total_cost || cdr.cost || 0),
          0
        );
        const totalMargin = (data.cdrs || []).reduce(
          (sum: number, cdr: CDR) =>
            sum + (cdr.margin_breakdown?.margin_amount || 0),
          0
        );
        setSummary({
          total_calls: data.total || 0,
          total_revenue: totalRevenue,
          total_margin: totalMargin,
        });
      }
    } catch (err: any) {
      setError(err.message || "Failed to load CDRs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCDRs();
  }, [page, rowsPerPage, startDate, endDate, accountFilter]);

  /** Handle export CDRs. */
  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
      });

      if (accountFilter) {
        params.append("account_filter", accountFilter);
      }

      const response = await resellerFetch(
        `${API_BASE_URL}/api/v1/resellers/me/cdrs/export?${params}`
      );

      if (!response.ok) {
        throw new Error("Failed to export CDRs");
      }

      // Download CSV
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cdrs_${startDate}_to_${endDate}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      setError(err.message || "Failed to export CDRs");
    } finally {
      setExporting(false);
    }
  };

  /** Handle page change. */
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  /** Handle rows per page change. */
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        My Call Detail Records
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Total Calls
              </Typography>
              <Typography variant="h5">
                {summary.total_calls.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Total Revenue
              </Typography>
              <Typography variant="h5" color="success.main">
                ${summary.total_revenue.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Total Margin
              </Typography>
              <Typography variant="h5" color="info.main">
                ${summary.total_margin.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Account Filter"
              placeholder="Filter by account..."
              value={accountFilter}
              onChange={(e) => setAccountFilter(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchCDRs}
                disabled={loading}
                fullWidth
              >
                Refresh
              </Button>
              <Button
                variant="outlined"
                startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}
                onClick={handleExport}
                disabled={exporting}
              >
                Export
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : totalCDRs === 0 && !accountFilter ? (
        <Paper
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 400,
            p: 4,
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <PhoneDisabledIcon
              sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
            />
            <Typography variant="h5" gutterBottom>
              CDRs Coming Soon
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Call detail records for your account will appear here once calls
              are made through the system.
            </Typography>
          </Box>
        </Paper>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Call ID</TableCell>
                <TableCell>Caller</TableCell>
                <TableCell>Callee</TableCell>
                <TableCell>Answer Time</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Account</TableCell>
                <TableCell>Cost</TableCell>
                <TableCell>Margin</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cdrs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No CDRs found for the selected filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                cdrs.map((cdr) => (
                  <TableRow key={cdr.id} hover>
                    <TableCell
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "0.75rem",
                        maxWidth: 150,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {cdr.call_id}
                    </TableCell>
                    <TableCell>{cdr.caller}</TableCell>
                    <TableCell>{cdr.callee}</TableCell>
                    <TableCell>
                      {format(new Date(cdr.answer_time), "yyyy-MM-dd HH:mm:ss")}
                    </TableCell>
                    <TableCell>{cdr.billsec}s</TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "0.8rem",
                      }}
                    >
                      {cdr.account}
                    </TableCell>
                    <TableCell>${cdr.cost.toFixed(4)}</TableCell>
                    <TableCell>
                      {cdr.margin_breakdown ? (
                        <Box>
                          <Typography variant="body2">
                            ${cdr.margin_breakdown.margin_amount?.toFixed(4)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {cdr.margin_breakdown.margin_percent}%
                          </Typography>
                        </Box>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="caption"
                        color={
                          cdr.hangup_cause === "NORMAL_CLEARING"
                            ? "success.main"
                            : "error.main"
                        }
                      >
                        {cdr.hangup_cause}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={totalCDRs}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      )}
    </Box>
  );
}
